import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthShell from '../main/AuthShell';
import Icon from '../../components/common/Icon';
import { apiRequest } from '../../utils';
import { clearCart } from '../shop/cart';
import './Storefront.css';

/* Storefront order confirmation (/:slug/confirmation).
   Two ways in: (1) straight from checkout with the result in router state (no
   online payment); (2) back from Stripe as a full page load — router state is
   gone, so we read ?order=<id> (usePersistUrlParams stashes it in sessionStorage)
   and fetch GET /api/storefront/order/<id>. Until the webhook has recorded the
   payment the order reads 'processing' — we poll a few times, then show
   "confirmation by email" with a pay-again fallback.
   Jobs: confirm + spam/safe-sender note, verify-email button, share the
   buy-a-seat link. */

const StorefrontConfirmation = () => {
  const { t } = useTranslation();
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const stateResult = location.state && location.state.result;
  const accent = location.state && location.state.color
    ? { backgroundColor: location.state.color } : undefined;

  const query = new URLSearchParams(location.search);
  const orderId = query.get('order') || sessionStorage.getItem('order');

  const [verifyState, setVerifyState] = useState('idle'); // idle|sending|sent|verified
  const [verifyError, setVerifyError] = useState(null);
  const [inviteCopied, setInviteCopied] = useState(false);
  // Pick-a-password step, for an account that has never had one.
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [pwError, setPwError] = useState(null);

  // Stripe-return path: fetch the order status by id and poll while it is still
  // processing (the webhook usually lands within a second or two).
  const [fetched, setFetched] = useState(null);
  const [phase, setPhase] = useState(
    stateResult ? 'ready' : (orderId ? 'loading' : 'empty'));
  const [payingAgain, setPayingAgain] = useState(false);
  const polls = useRef(0);

  useEffect(() => {
    if (stateResult || !orderId) return undefined;
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      try {
        const d = await apiRequest('GET', `/storefront/order/${orderId}`);
        if (cancelled) return;
        setFetched(d);
        // Paid, or never going to be paid here: an invoice order is settled by bank
        // transfer, so "unpaid" is its normal resting state and polling it would
        // spin six times and then accuse the buyer of an unfinished payment. Only an
        // order with a Stripe session pending is genuinely mid-payment (`processing`).
        if (d.paid || !d.processing) {
          setPhase('ready');
          if (d.event_id) clearCart(d.event_id);
          sessionStorage.removeItem('order');
          return;
        }
        // Not yet paid: keep polling a handful of times, then settle on the
        // "processing — we'll email you" state (the webhook is the truth).
        polls.current += 1;
        setPhase(polls.current >= 6 ? 'processing' : 'polling');
        if (polls.current < 6) timer = setTimeout(tick, 2500);
      } catch {
        if (!cancelled) setPhase('empty');
      }
    };
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [orderId, stateResult]);

  const result = stateResult || fetched;

  const payAgain = async () => {
    setPayingAgain(true);
    try {
      const d = await apiRequest('POST', '/storefront/pay-again',
        { order_id: orderId, slug });
      if (d.checkout_url) window.location.href = d.checkout_url;
    } catch (err) {
      setVerifyError(t(err.message, { defaultValue: err.message }));
      setPayingAgain(false);
    }
  };

  if (phase === 'loading' || phase === 'polling') {
    return (
      <AuthShell wide title={t('paymentProcessing', { defaultValue: 'Finishing your payment…' })}>
        <div className="auth__card" style={{ textAlign: 'center' }}>
          <span className="auth__spinner" />
          <p className="auth__hint">{t('paymentProcessingBody', {
            defaultValue: "We're confirming your payment — this only takes a moment." })}</p>
        </div>
      </AuthShell>
    );
  }

  if (!result) {
    return (
      <AuthShell wide title={t('orderPlaced')}>
        <div className="auth__card">
          <p>{t('storefrontNotFound')}</p>
          <Link to={`/${slug}`} className="auth__primary">{t('backToShop', { defaultValue: 'Back' })}</Link>
        </div>
      </AuthShell>
    );
  }

  // A basket with two team products makes two teams; the buyer sits on one.
  const multiTeam = (result.teams || []).length > 1;

  // Where the primary button goes once there is a session behind it. Same rules the
  // checkout's own JWT follows: a company buyer is the contact person (they assign
  // captains), a Bravos member has a member home, everyone else has a team.
  const primaryTarget = result.member_home ? '/member-home'
    : (result.is_company_order ? (result.bravos ? '/member-home' : '/') : '/team');

  // Confirming an invoice order by mail proves the address, so the server has just
  // signed them in — but almost none of these accounts has a password (our CRM made
  // most of them). Ask for one HERE, on the way to their team, while they are still
  // on the page: a mail telling them to set one later is a mail nobody opens.
  const savePassword = async () => {
    if (savingPw || !password) return;
    setSavingPw(true);
    setPwError(null);
    try {
      await apiRequest('POST', '/storefront/set-password', { password });
      localStorage.setItem('isLoggedIn', '1');
      window.dispatchEvent(new Event('auth-changed'));
      navigate(primaryTarget);
    } catch (err) {
      setPwError(t(err.message, { defaultValue: err.message }));
    } finally {
      setSavingPw(false);
    }
  };

  const verify = async () => {
    setVerifyState('sending');
    setVerifyError(null);
    try {
      // event_id gives the mail its brand: from-name + button in the event's
      // colours instead of the bare Hercules default.
      const res = await apiRequest('POST', '/send-verification',
        { event_id: (location.state && location.state.eventId) || (result && result.event_id) || null });
      setVerifyState(res.verified ? 'verified' : 'sent');
    } catch (err) {
      // Show WHY it failed instead of silently resetting the button.
      setVerifyError(t(err.message, { defaultValue: err.message }));
      setVerifyState('idle');
    }
  };

  return (
    <AuthShell
      wide
      title={t('orderPlaced')}
      subtitle={result.order_number || ''}
    >
      <div className="storefront">
        <div className="auth__card">
          <p className="auth__hint">
            <b>{t('orderTotal', { defaultValue: 'Total' })}: {result.currency} {Number(result.total).toFixed(2)}</b>
            {result.team_name ? <> · {result.team_name}</> : null}
          </p>

          {/* Payment not confirmed yet: tell them the truth and offer to retry. */}
          {phase === 'processing' ? (
            <>
              <p className="auth__hint"><Icon name="clock" /> {t('paymentProcessingEmail', {
                defaultValue: "Your payment is being processed — we'll email your confirmation and invoice as soon as it clears." })}</p>
              <button type="button" className="auth__primary" style={accent}
                      onClick={payAgain} disabled={payingAgain}>
                {payingAgain ? <span className="auth__spinner" /> : <Icon name="credit-card" />}
                {t('payNow', { defaultValue: 'Pay now' })}
              </button>
            </>
          ) : (
            <>
              <p className="auth__hint">{t('confirmationMailWithInvoice', {
                defaultValue: "Thanks! Your confirmation and invoice are on their way by email." })}</p>
              <p className="auth__hint"><Icon name="envelope" /> {t('checkSpamMarkSafe')}</p>
            </>
          )}

          {/* Only nag about verification when the account isn't already verified
              (server truth via result.email_verified) and the payment is settled. */}
          {phase !== 'processing' && !result.email_verified && (verifyState === 'sent' ? (
            <p className="auth__hint"><Icon name="check" /> {t('verificationMailSent')}</p>
          ) : verifyState === 'verified' ? (
            <p className="auth__hint"><Icon name="check" /> {t('emailAlreadyVerified')}</p>
          ) : (
            <>
              <button type="button" className="auth__primary" style={accent} onClick={verify}
                      disabled={verifyState === 'sending'}>
                {verifyState === 'sending'
                  ? <span className="auth__spinner" /> : <Icon name="envelope" />}
                {t('verifyMyEmail')}
              </button>
              {verifyError && <p className="auth__hint">{verifyError}</p>}
            </>
          ))}
        </div>

        {/* Bravos individual member: the personal member home is their next step, not a
            team. This is the "after payment you return to your home" path (a consumer Lab
            order makes the buyer an individual member). */}
        {result.member_home && (
          <Link to="/member-home" className="auth__primary" style={accent}>
            <Icon name="home" />
            {t('backToHome')}
          </Link>
        )}

        {/* Bought MORE than one team: the checkout seats the buyer on the first one
            only, so "go to my team" would quietly hide the rest. Name them here and
            send them to Home, which lists every team of their order and opens each
            one's players screen (where the invite link lives — the receipt stays a
            receipt). */}
        {!result.member_home && !result.is_company_order && multiTeam && (
          <>
            <div className="auth__card">
              <p className="auth__hint"><b>{t('yourTeamsTitle')}</b></p>
              {result.teams.map((tm) => (
                <p key={tm.team_id} className="auth__hint">
                  <Icon name="users" /> {tm.name}
                  {tm.open_seats > 0 ? ` · ${t('openSeatsCount', { n: tm.open_seats })}` : ''}
                </p>
              ))}
              <p className="auth__hint">{t('yourTeamsSub')}</p>
            </div>
            <Link to="/" className="auth__primary" style={accent}>
              <Icon name="users" />
              {t('manageMyTeams')}
            </Link>
          </>
        )}

        {/* No password on this account yet: the one step between the receipt and
            everything behind it. Replaces the primary button rather than sitting
            next to it — "go to my team" and "set a password" are the same action
            here, and offering both is how someone ends up locked out tomorrow. */}
        {result.needs_password && (
          <div className="auth__card">
            <b>{t('choosePasswordTitle')}</b>
            <p className="auth__hint">{t('choosePasswordBody', { email: result.email || '' })}</p>
            <div className="auth__field">
              <div className="auth__input-wrap">
                <input className="auth__input auth__input--with-eye"
                       type={showPassword ? 'text' : 'password'} value={password}
                       onChange={(e) => setPassword(e.target.value)}
                       autoComplete="new-password" placeholder={t('password')} />
                <button type="button" className="auth__eye"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={t(showPassword ? 'hidePassword' : 'showPassword')}>
                  <Icon name={showPassword ? 'eye-crossed' : 'eye'} />
                </button>
              </div>
            </div>
            {pwError && <p className="auth__hint">{pwError}</p>}
            <button type="button" className="auth__primary" style={accent}
                    onClick={savePassword} disabled={savingPw || !password}>
              {savingPw ? <span className="auth__spinner" /> : <Icon name="users" />}
              {result.is_company_order ? t('setPasswordAndAssign') : t('setPasswordAndGoToTeam')}
            </button>
          </div>
        )}

        {/* Primary next step — right up top so it's unmissable. Only for a buyer
            who actually holds a seat (a player). A company buyer is the CONTACT
            PERSON (besteller): they have no team to go to and can't do anything on
            the player screen, so never send them there — is_company_order guards it
            even when buyer_plays is absent (the Stripe-return fetch omits it). */}
        {/* Signed up alone: the next step is finding her team, not inviting one. */}
        {!result.needs_password && result.solo && (
          <div className="auth__card">
            <b>{t('soloConfirmTitle')}</b>
            <p className="auth__hint">{t('soloConfirmBody')}</p>
            <Link to="/meet-the-ladies" className="auth__primary" style={accent}>
              <Icon name="heart" />
              {t('meetTheLadies')}
            </Link>
          </div>
        )}
        {!result.needs_password && !result.member_home && result.buyer_plays !== false
          && !result.is_company_order && !multiTeam && !result.solo && (
          <Link to="/team" className="auth__primary" style={accent}>
            <Icon name="users" />
            {t('goToMyTeam')}
          </Link>
        )}

        {/* Company buyer: signed in as the contact person. A Bravos company runs
            everything from the member home (distribution, feedback, basket), so
            send them there under "manage my Bravos membership"; any other BU keeps
            the generic "add captains" prompt on the contact-person home. */}
        {result.is_company_order && !result.needs_password && (
          <Link to={result.bravos ? '/member-home' : '/'} className="auth__primary" style={accent}>
            <Icon name="user-crown" />
            {result.bravos ? t('manageMyBravos') : t('goAssignCaptains')}
          </Link>
        )}
        {/* The one link that DOES belong on the receipt: the buyer's invite link.
            The friend beside her is buying at the same moment — if she cannot share
            it here, that friend types the same team name and starts a namesake team
            of her own. That is exactly how one event got four "Sabrina's team"s.
            Free claim link when the seats are already paid, buy-a-seat otherwise;
            the flag comes from the server so the copy matches the link. */}
        {!result.member_home && !result.is_company_order && !result.solo
          && result.buyer_plays !== false && result.invite_link && (
          <div className="auth__card">
            <b>{result.invite_link_free ? t('inviteFreeTitle') : t('inviteFriendsTitle')}</b>
            <p className="auth__hint">
              {result.invite_link_free ? t('inviteFreeExplainer') : t('inviteCopyExplainer')}
            </p>
            <input className="auth__input" readOnly value={result.invite_link}
                   onFocus={(e) => e.target.select()} />
            <button type="button" className="auth__ghost" onClick={() => {
              navigator.clipboard.writeText(`${t('teamSeatShareText')}
${result.invite_link}`)
                .then(() => {
                  setInviteCopied(true);
                  setTimeout(() => setInviteCopied(false), 2000);
                })
                .catch(() => { /* clipboard blocked — the link is there to copy by hand */ });
            }}>
              <Icon name="copy" />
              {inviteCopied ? t('copied') : t('copyLink')}
            </button>
          </div>
        )}
      </div>
    </AuthShell>
  );
};

export default StorefrontConfirmation;
